import { uniqueId } from "lodash";
import {
  DependencyRules,
  CoRelationDependencyRules,
  Rule,
  RowDataReducerState,
  PrimitiveValueType,
  RuleExclusionConditionType,
  RowDataReducerAction,
  RowData,
  JsonSchemaItems,
} from "../../../types";
import { DEFAULT_DEPENDENCY_RULE } from "../evaluations";
import {
  getResolvedDynamicRowDataValue,
  dispatchUpdateField,
} from "./rowDataHelpers";
import {
  dispatchUpdateSchemaPropertyField,
  getDefaultSchemaPropertyValue,
} from "./schemaHelpers";

export const getApplicableRule = (
  dependencyRules: DependencyRules | CoRelationDependencyRules,
  value: string | null | boolean,
  rowIndex: number | undefined,
  propertySubKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
): Rule => {
  const rules =
    rowIndex !== undefined && propertySubKey !== undefined
      ? (dependencyRules as CoRelationDependencyRules)[propertySubKey]?.rules
      : (dependencyRules as DependencyRules).rules;

  const rule = findMatchingRule(rules, value);

  // Skip rule execution if exclusion criteria are met
  if (
    rule.ruleExclusionCriteria &&
    shouldSkipRuleExecution(rule.ruleExclusionCriteria, rowDataReducerState)
  ) {
    return DEFAULT_DEPENDENCY_RULE;
  }

  return rule;
};

const findMatchingRule = (
  rules: Rule[] | undefined,
  value: string | null | boolean,
): Rule => {
  return (
    rules?.find((rule) => rule.matches.includes(value)) ||
    DEFAULT_DEPENDENCY_RULE
  );
};

export const shouldSkipRuleExecution = (
  ruleExclusionCriteria: RuleExclusionConditionType,
  rowDataReducerState: RowDataReducerState,
): boolean => {
  const fieldsToValidate = Object.keys(ruleExclusionCriteria);
  return fieldsToValidate.some((field) => {
    const fieldValue = rowDataReducerState.rowData[field] as PrimitiveValueType;
    return ruleExclusionCriteria[field].matches.includes(fieldValue);
  });
};

export const triggerDependencyRule = (
  dispatch: React.Dispatch<RowDataReducerAction>,
  propertyKey: string,
  rowIndex: number | undefined,
  targetKey: string,
): void => {
  dispatchUpdateSchemaPropertyField(
    dispatch,
    "triggerDependencyRule",
    propertyKey,
    rowIndex,
    targetKey,
    uniqueId("triggerDependencyRule"),
  );
};

export const updateAndMaybeResetField = ({
  dispatch,
  propertyKey,
  rowIndex,
  selectedTabSectionKey,
  targetKey,
  enumValues,
  allowedValues,
  rowData,
}: {
  dispatch: React.Dispatch<RowDataReducerAction>;
  propertyKey: string;
  rowIndex: number | undefined;
  selectedTabSectionKey?: string;
  targetKey: string;
  enumValues: Array<string | null>;
  allowedValues?: string[];
  rowData: RowData;
}): void => {
  const existingValue = getResolvedDynamicRowDataValue({
    propertyKey,
    dynamicField: `$${targetKey}`,
    rowIndex,
    selectedTabSectionKey,
    rowData,
  }) as string | null;

  // For existing entries with a legacy value, include allowedValues so it remains selectable
  const isExistingEntryWithLegacyValue =
    existingValue !== null &&
    existingValue !== undefined &&
    allowedValues?.includes(existingValue as string) &&
    !enumValues.includes(existingValue);

  const schemaEnumValues = isExistingEntryWithLegacyValue
    ? Array.from(new Set([...enumValues, ...(allowedValues ?? [])]))
    : enumValues;

  dispatchUpdateSchemaPropertyField(
    dispatch,
    "enum",
    propertyKey,
    rowIndex,
    targetKey,
    schemaEnumValues,
  );

  const isValid =
    enumValues.includes(existingValue) ||
    allowedValues?.includes(existingValue as string);

  if (!isValid) {
    dispatchUpdateField({
      propertyKey,
      rowIndex,
      newFieldValue: null,
      selectedTabSectionKey,
      targetFieldKey: targetKey,
      dispatch,
    });
  } else {
    triggerDependencyRule(dispatch, propertyKey, rowIndex, targetKey);
  }
};

export const setFieldDisabled = (
  dispatch: React.Dispatch<RowDataReducerAction>,
  propertyKey: string,
  rowIndex: number | undefined,
  targetKey: string,
  disabled: boolean,
): void => {
  dispatchUpdateSchemaPropertyField(
    dispatch,
    "disabled",
    propertyKey,
    rowIndex,
    targetKey,
    disabled,
  );
};

export const disableAndClearField = (
  dispatch: React.Dispatch<RowDataReducerAction>,
  propertyKey: string,
  rowIndex: number | undefined,
  selectedTabSectionKey: string | undefined,
  targetKey: string,
  rowDataReducerState: RowDataReducerState,
): void => {
  const { rowData } = rowDataReducerState;

  setFieldDisabled(dispatch, propertyKey, rowIndex, targetKey, true);

  const defaultValue = getDefaultSchemaPropertyValue(
    propertyKey,
    rowIndex,
    targetKey,
    rowDataReducerState,
  );

  const existingValue = getResolvedDynamicRowDataValue({
    propertyKey,
    dynamicField: `$${targetKey}`,
    rowIndex,
    selectedTabSectionKey,
    rowData,
  }) as string | null;

  if (existingValue !== defaultValue) {
    dispatchUpdateField({
      propertyKey,
      rowIndex,
      newFieldValue: defaultValue,
      selectedTabSectionKey,
      targetFieldKey: targetKey,
      dispatch,
    });
  } else {
    triggerDependencyRule(dispatch, propertyKey, rowIndex, targetKey);
  }
};

// Returns ALL child keys for a corelation parent — used for disable/clear paths
export const getCoRelationChildKeys = (
  parentKey: string,
  rowDataReducerState: RowDataReducerState,
): string[] => {
  const parentSchema = rowDataReducerState.schema.properties[parentKey];
  return Object.keys(
    (parentSchema?.items as JsonSchemaItems)?.properties ?? {},
  );
};

// Returns only child keys that are NOT permanently disabled in the original schema
// Reads from resetState.schema to avoid being affected by runtime schema mutations
// caused by previous dependency rule executions (e.g. toggling applies_to repeatedly)
export const getEnableableCoRelationChildKeys = (
  parentKey: string,
  rowDataReducerState: RowDataReducerState,
): string[] => {
  const originalSchema =
    rowDataReducerState.resetState?.schema ?? rowDataReducerState.schema;

  const parentSchema = originalSchema.properties[parentKey];
  const properties = (parentSchema?.items as JsonSchemaItems)?.properties ?? {};

  return Object.entries(properties)
    .filter(([, childProp]) => childProp.disabled !== true)
    .map(([key]) => key);
};

// Disables the parent schema property (disables add button in RenderCoRelation)
// and empties its rowData array (clears all rows via useEffect in RenderCoRelation)
export const emptyCoRelationArray = (
  dispatch: React.Dispatch<RowDataReducerAction>,
  parentKey: string,
  selectedTabSectionKey: string | undefined,
): void => {
  // Disable parent → isAddButtonDisabled = true in RenderCoRelation
  dispatchUpdateSchemaPropertyField(
    dispatch,
    "disabled",
    parentKey,
    undefined,
    parentKey,
    true,
  );

  // Empty array + collect deleted object IDs in one atomic state update
  dispatch({
    type: "EMPTY_CORELATION_ARRAY",
    payload: {
      propertyKey: parentKey,
      selectedTabSectionKey,
    },
  });
};
